import type { CompareOperator } from "./types";

type CompareValue = string | number | boolean | string[];

// Type-aware comparison function
export const compare =
  (op: CompareOperator) =>
  (a: CompareValue, b: CompareValue): boolean => {
    // Handle null/undefined cases
    if (a == null || b == null) {
      switch (op) {
        case "eq":
          return a == b;
        case "neq":
          return a != b;
        default:
          return false;
      }
    }

    // Handle array values - convert to string representation for comparison
    const normalizeValue = (value: CompareValue): string | number | boolean => {
      if (Array.isArray(value)) {
        return value.join(",");
      }
      return value;
    };

    const normalizedA = normalizeValue(a);
    const normalizedB = normalizeValue(b);

    // For numeric operations, try to convert both values to numbers
    if (op === "lt" || op === "lte" || op === "gt" || op === "gte") {
      const numA =
        typeof normalizedA === "number" ? normalizedA : Number(normalizedA);
      const numB =
        typeof normalizedB === "number" ? normalizedB : Number(normalizedB);

      // If both can be converted to valid numbers, do numeric comparison
      if (!isNaN(numA) && !isNaN(numB) && isFinite(numA) && isFinite(numB)) {
        switch (op) {
          case "lt":
            return numA < numB;
          case "lte":
            return numA <= numB;
          case "gt":
            return numA > numB;
          case "gte":
            return numA >= numB;
        }
      }

      // Fall back to string comparison if numeric conversion fails
      const strA = String(normalizedA);
      const strB = String(normalizedB);
      switch (op) {
        case "lt":
          return strA < strB;
        case "lte":
          return strA <= strB;
        case "gt":
          return strA > strB;
        case "gte":
          return strA >= strB;
      }
    }

    // For equality operations, handle type-aware comparison
    if (op === "eq" || op === "neq") {
      let isEqual: boolean;

      // If both are the same type, use direct comparison
      if (typeof normalizedA === typeof normalizedB) {
        isEqual = normalizedA === normalizedB;
      } else {
        // Try numeric comparison first if both can be numbers
        const numA = Number(normalizedA);
        const numB = Number(normalizedB);
        if (!isNaN(numA) && !isNaN(numB) && isFinite(numA) && isFinite(numB)) {
          isEqual = numA === numB;
        } else {
          // Fall back to string comparison
          isEqual = String(normalizedA) === String(normalizedB);
        }
      }

      return op === "eq" ? isEqual : !isEqual;
    }

    // For string operations, convert to strings
    const strA = String(normalizedA);
    const strB = String(normalizedB);

    switch (op) {
      case "in":
        return strA.includes(strB);
      case "nin":
        return !strA.includes(strB);
      case "matches":
        try {
          // Regex pattern is sourced from trusted backend configuration, not user input.
          // codacy-disable-next-line javascript.lang.security.detect-non-literal-regexp.detect-non-literal-regexp
          const regex = new RegExp(strB);
          return !regex.test(strA);
        } catch (error) {
          // Treat invalid regex patterns as non-matching (same as regex.test(strA) === false)
          // and avoid crashing validation

          console.warn(
            'Invalid regex pattern in compare "matches" operator:',
            strB,
            error,
          );
          return true;
        }
      default:
        throw new Error("Unsupported comparison operator");
    }
  };

// Cache the comparison functions
const compareFunctions = new Map();

export const getCompareFunction = (operator: CompareOperator) => {
  if (!compareFunctions.has(operator)) {
    compareFunctions.set(operator, compare(operator));
  }
  return compareFunctions.get(operator) as (
    a: CompareValue,
    b: CompareValue,
  ) => boolean;
};
